import type { ReactNode } from "react";
import VerifiedIcon from "@mui/icons-material/Verified";
import {
  Alert,
  Box,
  Button,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { match, P } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  ASSESSMENT_HISTORY_PAGE_LIMIT_MAX,
  type Assessment,
  type AssessmentId,
  type SummaryGrouping,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { AssessmentSummary } from "../../components/AssessmentSummary/AssessmentSummary.js";
import { useUser } from "../../Context/UserContext.js";
import {
  createRoute,
  SUMMARY_GROUPING_PARAMETER,
} from "../../routing/createRoute.js";
import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import {
  assessorNameOf,
  finishedAssessmentActions,
} from "../Persons/involvementManagement.js";
import { useAssessment, useInstrumentForm } from "./AssessmentContext.js";
import {
  assessmentPdfDownloadOf,
  type PdfDownloadOutcome,
} from "./assessmentPdfDownload.js";
import { AssessmentViewHeader, SHORT_DATE } from "./AssessmentViewHeader.js";
import {
  comparisonCandidates,
  familyOfName,
  type ComparisonCandidate,
} from "./comparisonCandidates.js";
import { fullHistoryHrefOf } from "./fullHistoryHrefOf.js";
import { latestSubmission } from "./latestSubmission.js";
import { momentOf, type SummaryFacts } from "./summaryFacts.js";
import {
  summaryStateOf,
  type ComparedReading,
  type ComparisonProblem,
  type SummaryBody,
} from "./summaryState.js";
import {
  needsForFocusOf,
  useAssessmentReadings,
} from "./useAssessmentReadings.js";

const groupingOf = (value: string | null): SummaryGrouping =>
  value === "domain" ? "domain" : "rating";

/**
 * An assessment's summary, alone or compared with an earlier one, at an
 * address of its own: the assessment and what it is compared with are in the
 * path, and the grouping in the query, so a link holds the whole view.
 */
export const AssessmentSummaryPage = () => {
  const { assessment } = useAssessment();
  const { comparedId } = useParams();

  if (assessment === undefined) {
    return null;
  }

  return comparedId === undefined ? (
    <SummaryOf assessment={assessment} />
  ) : (
    // Keyed, so asking for another comparison reads it afresh.
    <ComparedSummaryOf
      key={comparedId}
      assessment={assessment}
      comparedId={comparedId}
    />
  );
};

const ComparedSummaryOf = ({
  assessment,
  comparedId,
}: {
  assessment: Assessment;
  comparedId: string;
}) => {
  const [{ data, loading, response }] = apiHooks.assessment.byId(comparedId);
  // A 4xx body resolves onto `data`, so it is only taken from a 200.
  const compared = response?.status === 200 ? data : undefined;
  const { loading: form, retry } = useInstrumentForm(compared);

  const reading = (): ComparedReading => {
    if (compared !== undefined) {
      return { kind: "read", assessment: compared, form };
    }

    return loading ? { kind: "loading" } : { kind: "unreadable" };
  };

  return (
    <SummaryOf
      assessment={assessment}
      comparedId={comparedId}
      compared={reading()}
      onRetryCompared={retry}
    />
  );
};

const SummaryOf = ({
  assessment,
  comparedId,
  compared,
  onRetryCompared,
}: {
  assessment: Assessment;
  /**
   * The id the address names to compare with, as given, whether or not the
   * assessment it names could be read.
   */
  comparedId?: string | undefined;
  compared?: ComparedReading | undefined;
  onRetryCompared?: (() => void) | undefined;
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const grouping = groupingOf(searchParams.get(SUMMARY_GROUPING_PARAMETER));
  const { user } = useUser();
  const { loading: currentForm, retry } = useInstrumentForm(assessment);
  const [{ data: person, response: personResponse }] = apiHooks.person.byId(
    assessment.personId,
  );
  // A 4xx body resolves onto `data`, so each is only taken from a 200.
  const read = personResponse?.status === 200 ? person : undefined;
  const [{ data: involvements, response: involvementsResponse }] =
    apiHooks.involvement.forPerson(assessment.personId);
  // Given the record, so a change to anything merged into it is heard too.
  const [{ data: history, response: historyResponse }] =
    apiHooks.assessment.historyForPerson(assessment.personId, read, {
      limit: ASSESSMENT_HISTORY_PAGE_LIMIT_MAX,
    });

  const known = involvementsResponse?.status === 200 ? involvements : undefined;
  const finished =
    historyResponse?.status === 200 ? history?.results : undefined;

  const state = summaryStateOf({
    current: assessment,
    currentForm,
    compared,
    person: read,
    involvements: known,
    viewer: user,
  });
  const submission = latestSubmission(assessment);
  const mayRead = finishedAssessmentActions(user, known, read);
  const candidates =
    finished === undefined || submission === undefined
      ? undefined
      : comparisonCandidates({
          history: finished,
          assessmentId: assessment.id,
          instrumentName: assessment.instrument.name,
          before: momentOf({ assessment, submission }).at,
          mayRead: (candidate) => mayRead(candidate).view,
          assessorOf: (candidate) => assessorNameOf(candidate, user, known),
        });

  const readings = useAssessmentReadings(
    (candidates ?? []).map(({ assessmentId }) => assessmentId),
  );
  const family = familyOfName(assessment.instrument.name);

  return (
    <AssessmentSummaryPageContent
      facts={state.facts}
      body={state.body}
      problem={state.problem}
      grouping={grouping}
      candidates={candidates}
      needsFocus={needsForFocusOf(readings)}
      family={family}
      comparedWith={comparedId}
      fullAssessmentHref={createRoute.assessmentFull(assessment.id)}
      onDownloadPdf={assessmentPdfDownloadOf(assessment)}
      historyHref={fullHistoryHrefOf({
        assessment,
        involvements: known,
        family,
      })}
      onGroup={(chosen) => {
        // A change of grouping replaces the entry: it is the same view. Only
        // the query changes, so the path keeps whatever it compares with.
        setSearchParams(
          chosen === "domain" ? { [SUMMARY_GROUPING_PARAMETER]: chosen } : {},
          { replace: true },
        );
      }}
      onCompare={(chosen) => {
        navigate(
          createRoute.assessmentSummary(assessment.id, {
            comparedWith: chosen,
            grouping,
          }),
        );
      }}
      onRetry={() => {
        retry();
        onRetryCompared?.();
      }}
    />
  );
};

/** The value the comparison control holds where nothing is compared with. */
const NONE = "none";

const TAG_STYLE = {
  px: 0.5,
  borderRadius: 0.5,
  fontSize: "0.625rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  bgcolor: "action.selected",
  color: "text.primary",
} as const;

const HEADING_STYLE = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
} as const;

/** How many needs an earlier assessment had for focus, against the most any of them had. */
const NeedsBar = ({
  count,
  most,
}: {
  count: number | undefined;
  most: number;
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ width: 128, flexShrink: 0 }}
    >
      <Box
        aria-hidden
        sx={{ flexGrow: 1, height: 6, bgcolor: "divider", borderRadius: 3 }}
      >
        {count === undefined ? null : (
          <Box
            sx={{
              width: `${String((count / most) * 100)}%`,
              height: 1,
              bgcolor: "chart.needs",
              borderRadius: 3,
            }}
          />
        )}
      </Box>
      <Typography
        variant="body2"
        component="span"
        sx={{ fontWeight: 600, minWidth: 16, textAlign: "right" }}
      >
        {count === undefined ? "" : String(count)}
        <Box component="span" sx={VISUALLY_HIDDEN}>
          {count === undefined
            ? ""
            : ` ${t("assessment.summary.compare.needsFor")}`}
        </Box>
      </Typography>
    </Stack>
  );
};

/**
 * What follows a candidate's date: its tags, or whose contribution it is.
 * Each part opens with a space of its own, outside its styled span, so the
 * option's name reads as separate words without a translation carrying the
 * space or a tag drawing it inside its background.
 */
const CandidateNote = ({ candidate }: { candidate: ComparisonCandidate }) => {
  const { t } = useTranslation();

  return match(candidate)
    .with({ kind: "definitive" }, ({ justBefore, yearAgo }) => (
      <>
        {justBefore ? (
          <>
            {" "}
            <Box component="span" sx={TAG_STYLE}>
              {t("assessment.summary.compare.tag.justBefore")}
            </Box>
          </>
        ) : null}
        {yearAgo ? (
          <>
            {" "}
            <Box component="span" sx={TAG_STYLE}>
              {t("assessment.summary.compare.tag.yearAgo")}
            </Box>
          </>
        ) : null}
      </>
    ))
    .with({ kind: "contribution" }, ({ assessor }) => (
      <>
        {" "}
        <Typography variant="body2" component="span" color="text.secondary">
          {assessor === undefined ? (
            t("assessment.summary.compare.contribution")
          ) : (
            <>
              <Box component="span" sx={VISUALLY_HIDDEN}>
                {t("assessment.summary.compare.contributionBy")}
              </Box>{" "}
              {assessor}
            </>
          )}
        </Typography>
      </>
    ))
    .exhaustive();
};

const CompareControl = ({
  candidates,
  needsFocus,
  family,
  comparedWith,
  comparedAt,
  onCompare,
}: {
  candidates: readonly ComparisonCandidate[] | undefined;
  /** How many needs each candidate had for focus, by id, once it is read. */
  needsFocus: ReadonlyMap<AssessmentId, number>;
  /** The family of instruments the candidates are on. */
  family: string | undefined;
  comparedWith: string | undefined;
  /** When the assessment compared with is described as of, where it is known. */
  comparedAt: Date | undefined;
  onCompare: (assessmentId: AssessmentId | undefined) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const chosen = candidates?.find(
    ({ assessmentId }) => assessmentId === comparedWith,
  );
  const offered = candidates ?? [];
  const most = Math.max(1, ...needsFocus.values());
  /** What the control shows as chosen. */
  const shown = (): string => {
    if (comparedWith === undefined) {
      return t("assessment.summary.compare.none");
    }

    if (chosen !== undefined) {
      const date = SHORT_DATE.format(chosen.finishedAt);

      return match(chosen)
        .with({ kind: "definitive", justBefore: true }, () =>
          t("assessment.summary.compare.justBefore", { date }),
        )
        .with({ kind: "definitive" }, () => date)
        .with({ kind: "contribution" }, () =>
          t("assessment.summary.compare.chosenContribution", { date }),
        )
        .exhaustive();
    }

    return comparedAt === undefined
      ? t("assessment.summary.compare.describesAny")
      : SHORT_DATE.format(comparedAt);
  };

  const describes = (): string => {
    if (comparedWith === undefined) {
      return candidates === undefined
        ? ""
        : t("assessment.summary.compare.candidates", {
            count: candidates.length,
          });
    }

    if (chosen === undefined) {
      return t("assessment.summary.compare.describesAny");
    }

    return match(chosen)
      .with({ kind: "definitive", justBefore: true }, () =>
        t("assessment.summary.compare.describesJustBefore"),
      )
      .with({ kind: "definitive" }, () =>
        t("assessment.summary.compare.describesEarlier"),
      )
      .with({ kind: "contribution", assessor: P.string }, ({ assessor }) =>
        t("assessment.summary.compare.describesContribution", {
          name: assessor,
        }),
      )
      .with({ kind: "contribution" }, () =>
        t("assessment.summary.compare.describesContributionAnyone"),
      )
      .exhaustive();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}
    >
      <Typography
        id="summary-compare-with"
        variant="body2"
        sx={{ fontWeight: 600 }}
      >
        {t("assessment.summary.compare.label")}
      </Typography>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={comparedWith ?? NONE}
          sx={{ fontSize: "0.875rem" }}
          labelId="summary-compare-with"
          renderValue={shown}
          MenuProps={{
            slotProps: {
              paper: { sx: { maxHeight: 400 } },
              list: {
                // Kept out of the options and hidden from a screen reader:
                // each option already says what its count is of.
                subheader:
                  offered.length === 0 ? undefined : (
                    <ListSubheader
                      aria-hidden
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 4,
                        lineHeight: 2.75,
                      }}
                    >
                      <Box component="span" sx={HEADING_STYLE}>
                        {family === undefined
                          ? t("assessment.summary.compare.listHeadingAny")
                          : t("assessment.summary.compare.listHeading", {
                              family,
                            })}
                      </Box>
                      <Box component="span" sx={HEADING_STYLE}>
                        {t("assessment.summary.compare.needsHeading")}
                      </Box>
                    </ListSubheader>
                  ),
              },
            },
          }}
          onChange={(event) => {
            const { value } = event.target;

            if (value === NONE) {
              void onCompare(undefined);
              return;
            }

            // A select reports no change for the option already chosen,
            // which the address's own option always is, so a change picks a
            // candidate.
            const candidate = offered.find(
              ({ assessmentId }) => assessmentId === value,
            );

            // Unreachable while the select offers only NONE and `offered`;
            // kept to fail loudly should it offer anything else.
            /* v8 ignore if */
            if (candidate === undefined) {
              throw new Error(
                "Expected a change of comparison to pick a candidate",
              );
            }

            void onCompare(candidate.assessmentId);
          }}
        >
          {offered.map((candidate) => (
            <MenuItem
              key={candidate.assessmentId}
              value={candidate.assessmentId}
              sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}
            >
              {/* Spaced so the option's name reads as words, not run together. */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                {/* Held open for a contribution too, so every date lines up. */}
                <Box
                  component="span"
                  sx={{ width: 14, display: "inline-flex", flexShrink: 0 }}
                >
                  {candidate.kind === "definitive" ? (
                    <>
                      <VerifiedIcon
                        titleAccess={t("assessment.summary.compare.definitive")}
                        sx={{ fontSize: 14, color: "primary.main" }}
                      />{" "}
                    </>
                  ) : null}
                </Box>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: 700 }}
                >
                  {SHORT_DATE.format(candidate.finishedAt)}
                </Typography>
                <CandidateNote candidate={candidate} />
              </Stack>{" "}
              <NeedsBar
                count={needsFocus.get(candidate.assessmentId)}
                most={most}
              />
            </MenuItem>
          ))}
          {
            // An assessment the address names but the list does not offer is
            // still shown as the one chosen, so the control never claims a
            // comparison is something it is not.
            comparedWith === undefined || chosen !== undefined ? null : (
              <MenuItem value={comparedWith}>{shown()}</MenuItem>
            )
          }
          <MenuItem
            value={NONE}
            sx={{
              borderTop: offered.length === 0 ? 0 : 1,
              borderColor: "divider",
              textDecoration: "underline",
            }}
          >
            {t("assessment.summary.compare.none")}
          </MenuItem>
        </Select>
      </FormControl>
      {comparedWith === undefined ? null : (
        <Button
          variant="text"
          size="small"
          onClick={() => void onCompare(undefined)}
          sx={{ textTransform: "none", textDecoration: "underline" }}
        >
          {t("assessment.summary.compare.clear")}
        </Button>
      )}
      <Typography variant="body2" color="text.secondary">
        {describes()}
      </Typography>
    </Stack>
  );
};

const BodyShown = ({
  body,
  grouping,
  fullAssessmentHref,
  onGroup,
  onRetry,
}: {
  body: SummaryBody;
  grouping: SummaryGrouping;
  fullAssessmentHref: string;
  onGroup: (grouping: SummaryGrouping) => Promisable<void>;
  onRetry: () => void;
}): ReactNode => {
  const { t } = useTranslation();

  return match(body)
    .with({ kind: "loading" }, () => (
      <Typography color="text.secondary">
        {t("assessment.summary.loading")}
      </Typography>
    ))
    .with({ kind: "unavailable" }, () => (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            {t("assessment.summary.retry")}
          </Button>
        }
      >
        {t("assessment.summary.formUnavailable")}
      </Alert>
    ))
    .with({ kind: "withheld" }, () => (
      <Alert severity="info">{t("assessment.summary.withheld")}</Alert>
    ))
    .with({ kind: "not_submitted" }, () => (
      <Alert severity="info">{t("assessment.summary.notSubmitted")}</Alert>
    ))
    .with({ kind: "uncategorised" }, () => (
      <Alert
        severity="info"
        action={
          <Button
            color="inherit"
            size="small"
            component={RouterLink}
            to={fullAssessmentHref}
          >
            {t("assessment.summary.tabs.full")}
          </Button>
        }
      >
        {t("assessment.summary.uncategorised")}
      </Alert>
    ))
    .with({ kind: "summarised" }, ({ summary }) => (
      <AssessmentSummary
        summary={summary}
        grouping={grouping}
        onGroup={onGroup}
      />
    ))
    .exhaustive();
};

/**
 * The summary page's rendering, with everything it shows injected so a story
 * or a test can show any state of it without an API client.
 */
export const AssessmentSummaryPageContent = ({
  facts,
  body,
  problem,
  grouping,
  candidates,
  needsFocus,
  family,
  comparedWith,
  fullAssessmentHref,
  historyHref,
  onDownloadPdf,
  onGroup,
  onCompare,
  onRetry,
}: {
  /** Absent where `summaryStateOf` gives none. */
  facts?: SummaryFacts | undefined;
  body: SummaryBody;
  /** Why the assessment asked to compare with is not compared with. */
  problem?: ComparisonProblem | undefined;
  grouping: SummaryGrouping;
  /** The earlier assessments to offer; undefined until read. */
  candidates: readonly ComparisonCandidate[] | undefined;
  /** How many needs each candidate had for focus, by id, once it is read. */
  needsFocus: ReadonlyMap<AssessmentId, number>;
  /** The family of instruments the candidates are on. */
  family: string | undefined;
  /** The id the address names to compare with, if any, as given. */
  comparedWith: string | undefined;
  fullAssessmentHref: string;
  /** Absent where the history is not offered. */
  historyHref?: string | undefined;
  /** Absent where the assessment is not offered as a PDF. */
  onDownloadPdf?: (() => Promisable<PdfDownloadOutcome>) | undefined;
  onGroup: (grouping: SummaryGrouping) => Promisable<void>;
  /** Asks for a comparison with the assessment named, or none. */
  onCompare: (assessmentId: AssessmentId | undefined) => Promisable<void>;
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  const comparing = problem === undefined && comparedWith !== undefined;

  return (
    <Box>
      <AssessmentViewHeader
        facts={facts}
        view={{ kind: "summary", fullHref: fullAssessmentHref }}
        historyHref={historyHref}
        onDownloadPdf={onDownloadPdf}
      />
      <CompareControl
        candidates={candidates}
        needsFocus={needsFocus}
        family={family}
        comparedWith={comparedWith}
        comparedAt={comparing ? facts?.moments[0]?.at : undefined}
        onCompare={onCompare}
      />
      <Stack spacing={2} sx={{ p: 2, bgcolor: "background.subtle" }}>
        {problem === undefined ? null : (
          <Alert severity="warning">
            {t(`assessment.summary.compare.problem.${problem}`)}
          </Alert>
        )}
        <BodyShown
          body={body}
          grouping={grouping}
          fullAssessmentHref={fullAssessmentHref}
          onGroup={onGroup}
          onRetry={onRetry}
        />
      </Stack>
    </Box>
  );
};
